import { Injectable } from "@angular/core";
import { SIDE_NAV_ITEMS } from "@core/layout/components/side-nav/constants/side-nav-items";
import {
	AngularRoutesEnum,
	AppRoutesEnum,
	DojoRoutesEnum,
	VersionRouteEnum,
} from "@core/layout/enums/layout-routes.enum";
import { SideNavRouteEnum } from "@core/layout/components/side-nav/enums/side-nav-route.enum";
import { SideNavItem } from "@core/layout/components/side-nav/models/side-nav-item.model";
import { SIDE_NAV_ADMIN_HOTLINE_ITEMS } from "@core/layout/components/side-nav/constants/side-nav-admin-hotline-items";
import { SIDE_NAV_ADMIN_MARKET_ITEMS } from "@core/layout/components/side-nav/constants/side-nav-admin-market-items";
import { SIDE_NAV_ADMIN_DEV_ITEMS } from "@core/layout/components/side-nav/constants/side-nav-admin-dev-items";
import { FullClient, FullClientType } from "@models/full-client.model";
import { SideNavIds } from "@core/layout/components/side-nav/enums/side-nav-ids.enum";
import { SiteFonctionnalite } from "@jack-sparrow-libraries/emotion-models";

@Injectable({
	providedIn: "root",
})
export class SideNavService {
	readonly routesWhereAddOptionsAreHidden = [
		"/v2/" + AngularRoutesEnum.ADMIN_DEV,
		DojoRoutesEnum.ADMIN_HOTLINE.toString(),
		DojoRoutesEnum.ADMIN_MARKET.toString(),
		`${DojoRoutesEnum.ANNUAIRE}/${DojoRoutesEnum.MUTUELLES}`,
	] as const;

	generateItems(sideNavRoute: SideNavRouteEnum = SideNavRouteEnum.DEFAULT, fullClient: FullClient): SideNavItem[] {
		switch (sideNavRoute) {
			case SideNavRouteEnum.ADMIN_HOTLINE:
				return SIDE_NAV_ADMIN_HOTLINE_ITEMS;
			case SideNavRouteEnum.ADMIN_MARKET:
				return SIDE_NAV_ADMIN_MARKET_ITEMS;
			case SideNavRouteEnum.ADMIN_DEV:
				return SIDE_NAV_ADMIN_DEV_ITEMS;
			default: {
				const hasPaiementGaranti = !!fullClient?.permissions.includes(SiteFonctionnalite.PaiementGaranti);
				return SIDE_NAV_ITEMS.map((item) => ({
					...item,
					enfants: item.enfants
						?.filter((enfant) => this.filterEnfant(enfant, fullClient))
						.map((enfant) =>
							enfant.id === SideNavIds.PAIEMENT_REJETS && hasPaiementGaranti
								? { ...enfant, label: "Mes impayés" }
								: enfant,
						),
				}));
			}
		}
	}

	private filterEnfant(item: SideNavItem, fullClient: FullClient): boolean {
		if (!fullClient) {
			return false;
		}
		switch (item.id) {
			case SideNavIds.ORDONNANCES_PRESCRIPTION_IPA:
				return fullClient.permissions.includes(SiteFonctionnalite.PrescriptionMedicamenteuse);
			case SideNavIds.PATIENTELE_COORDINATION:
				return fullClient.permissions.includes("TimelineV2Web" as SiteFonctionnalite);
			case SideNavIds.CABINET_REMPLACANTS:
			case SideNavIds.PAIEMENT_EFACTURES:
				return fullClient.type !== FullClientType.REMPLACANT;
		}

		return true;
	}

	getSideNavRouteEnumFromUrl(link: string): SideNavRouteEnum {
		const url = new URL(link, location.origin);
		if (!link) {
			return SideNavRouteEnum.DEFAULT;
		} else if (
			url.pathname.startsWith(
				"/" + AppRoutesEnum.APP + "/" + VersionRouteEnum.V2 + "/" + AngularRoutesEnum.ADMIN_DEV,
			)
		) {
			return SideNavRouteEnum.ADMIN_DEV;
		} else if (url.hash.startsWith("#" + DojoRoutesEnum.ADMIN_MARKET)) {
			return SideNavRouteEnum.ADMIN_MARKET;
		} else if (
			url.pathname.startsWith(
				"/" + AppRoutesEnum.APP + "/" + VersionRouteEnum.V2 + "/" + AngularRoutesEnum.ADMIN,
			) ||
			url.hash.startsWith("#" + DojoRoutesEnum.ADMIN_HOTLINE) ||
			url.hash === "#" + `${DojoRoutesEnum.ANNUAIRE}/${DojoRoutesEnum.MUTUELLES}`
		) {
			return SideNavRouteEnum.ADMIN_HOTLINE;
		} else {
			return SideNavRouteEnum.DEFAULT;
		}
	}
}
